import type { Client, Fax } from '../types/client';
import type { DecryptedItem } from '../types/items';
import type { StatisticRecord, StatisticsFilter } from '../types/statistics/statistics';
import type { Field } from '../types/form';
import formatter from '../helpers/formatter';

const getStatisticByOptions = (
  clients: DecryptedItem<Client>[],
  key: keyof Client,
  formFields: Field<Client>[]
): StatisticRecord => {
  if (key === 'measures' || key === 'faxes' || key === 'contactAttempts' || key === 'documents') return {};
  const options = formatter.getFieldSelectOptions(key, formFields);
  const notSpecified = clients.filter((client) => client.payload[key] == null);

  const numberOfOptions = options.reduce((statistics, option) => {
    const clientByOption = clients.filter((client) => {
      const clientByKey = client.payload[key];
      if (typeof clientByKey === 'string') {
        return clientByKey != null && clientByKey === option.value;
      }
      if (Array.isArray(clientByKey)) {
        return clientByKey != null && clientByKey.includes(option.value);
      }
    });
    return { ...statistics, [option.value]: clientByOption.length };
  }, {});
  return { ...numberOfOptions, notSpecified: notSpecified.length };
};

const getStatisticByFaxOptions = (faxes: Fax[], key: keyof Fax, formFields: Field<Fax>[]): StatisticRecord => {
  const options = formatter.getFieldSelectOptions(key, formFields);
  const notSpecified = faxes.filter((client) => client[key] == null);

  const numberOfOptions = options.reduce((statistics, option) => {
    const faxByOption = faxes.filter((fax) => {
      const faxByKey = fax[key];
      if (typeof faxByKey === 'string') {
        return faxByKey != null && faxByKey === option.value;
      }
    });
    return { ...statistics, [option.value]: faxByOption.length };
  }, {});
  return { ...numberOfOptions, notSpecified: notSpecified.length };
};

const getNumberOfValues = (clients: DecryptedItem<Client>[], key: keyof Client, values?: string[]): number => {
  if (key === 'measures' || key === 'faxes' || key === 'contactAttempts' || key === 'documents') return 0;
  const filteresClients = clients.filter(({ payload }) => {
    const currentValue = payload[key];
    if (typeof currentValue === 'boolean') {
      return currentValue;
    }
    if (typeof currentValue === 'string' && values != null) {
      return values.includes(currentValue);
    }
    if (Array.isArray(currentValue) && values != null) {
      const filteredValues = values.filter((value) => currentValue.includes(value));
      return filteredValues.length > 0;
    }
    return false;
  });
  return filteresClients.length;
};

const getSumOfValues = (clients: DecryptedItem<Client>[], key: keyof Client): number => {
  if (key === 'measures' || key === 'faxes' || key === 'contactAttempts') return 0;
  return clients.reduce((prevValue: number, { payload }) => {
    const value = payload[key];
    if (typeof value === 'number') {
      return prevValue + value;
    }
    if (typeof value === 'string') {
      const valueToInt = parseInt(value);
      const amount = isNaN(valueToInt) ? 0 : valueToInt;
      return prevValue + amount;
    }
    return prevValue;
  }, 0);
};

const getStringValueAsArray = (clients: DecryptedItem<Client>[], key: keyof Client): string[] => {
  return clients.reduce((prevValue: string[], client) => {
    const value = client.payload[key];
    if (value != null && typeof value === 'string' && !prevValue.includes(value)) {
      return prevValue.concat([value]);
    }
    return prevValue;
  }, []);
};

const defaultFilter: StatisticsFilter = {
  year: `${new Date().getFullYear()}`,
  advisor: '',
};

const statistics = {
  getStatisticByOptions,
  getStatisticByFaxOptions,
  getNumberOfValues,
  getSumOfValues,
  getStringValueAsArray,
  defaultFilter,
};

export default statistics;
